import { RoutalPalette, RoutalSelect } from '@nx-smartmonkey/ui';
import React, { useEffect, useState } from 'react';

import { ContextualMenu } from '../../../../../components/menus/ContextualMenu';
import { User } from '../../../../../domain/backoffice/user/User';
import Text from '../../../../../react-components/Text';
import { UpdateSubscriptionExtraLimitsDialog } from '../../../dialogs/UpdateSubscriptionExtraLimitsDialog';
import { useValidateUser } from '../../../hooks/users/useValidateUser';
import { generateStripeCustomerLink } from '../../../shared/SystemsInfoBox';
import { BackofficeClientsOrganizationsManageContractDialog } from '../../organizations/shared/BackofficeClientsOrganizationsManageContractDialog';
import { BackofficeClientsOrganizationsManageGeocodeDialog } from '../../organizations/shared/BackofficeClientsOrganizationsManageGeocodeDialog';
import { BackofficeClientsOrganizationsManageSMSDialog } from '../../organizations/shared/BackofficeClientsOrganizationsManageSMSDialog';
import { BackofficeClientsUsersEditEmailDialog } from './BackofficeClientsUsersEditEmailDialog';
import { BackofficeClientsUsersEditPasswordDialog } from './BackofficeClientsUsersEditPasswordDialog';
import { CancelSubscriptionDialog } from './CancelSubscriptionDialog';
import { CreateTrialSubscriptionDialog } from './CreateTrialSubscriptionDialog';

type BackofficeClientsUserActionsMenuContextAnchor = {
  screenPosition: {
    top: number;
    left: number;
  };
};

type BackofficeClientsUserActionsMenuType = `contextual` | `popover`;

type BackofficeClientsUserActionsMenuProps = {
  user: User;
  type?: BackofficeClientsUserActionsMenuType;
  contextAnchor?: BackofficeClientsUserActionsMenuContextAnchor;
  showUpdateEmail?: boolean;
  showUpdatePassword?: boolean;
  showValidate?: boolean;
  showManageSMS?: boolean;
  showManageGeocodeCredit?: boolean;
  showManageContract?: boolean;
  showChangePlan?: boolean;
  showCancelSubscription?: boolean;
  showChangeExtraLimits?: boolean;
  showCreateSubscriptionTrial?: boolean;
  popoverProps?: {
    hideArrow?: boolean;
    style?: React.CSSProperties;
    containerStyle?: React.CSSProperties;
    content?: React.ReactElement;
  };
  refreshAfterUpdate?: () => void;
  setContextAnchor?: (context?: BackofficeClientsUserActionsMenuContextAnchor) => void;
};

export const BackofficeClientsUserActionsMenu = ({
  user,
  type = `contextual` as BackofficeClientsUserActionsMenuType,
  contextAnchor = {
    screenPosition: {
      top: 0,
      left: 0,
    },
  },
  showUpdateEmail = true,
  showUpdatePassword = true,
  showValidate = true,
  showManageSMS = true,
  showManageGeocodeCredit = true,
  showManageContract = true,
  showChangePlan = true,
  showCancelSubscription = true,
  showChangeExtraLimits = true,
  showCreateSubscriptionTrial = true,
  popoverProps,
  refreshAfterUpdate,
  setContextAnchor = () => {},
}: BackofficeClientsUserActionsMenuProps) => {
  const { run: validateUser, success: validationSuccess } = useValidateUser();
  const [dialog, setDialog] = useState<
    | `change_password`
    | `change_email`
    | `manage_sms_limit`
    | `manage_geocode_credit`
    | `manage_contract`
    | `change_extra_limits`
    | `cancel_subscription`
    | `create_trial_subscription`
    | undefined
  >(undefined);

  useEffect(() => {
    if (validationSuccess) {
      if (setContextAnchor) {
        setContextAnchor();
      }

      if (refreshAfterUpdate) {
        refreshAfterUpdate();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationSuccess]);

  if (!user) return null;

  return (
    <>
      {type === `popover` && (
        <RoutalSelect
          id="routes-select-button-popover"
          hideArrow={popoverProps?.hideArrow}
          style={{
            background: `transparent`,
            ...(popoverProps?.style ?? {}),
          }}
          containerStyle={popoverProps?.containerStyle}
          popoverProps={{
            maxHeight: `unset`,
            options: [
              ...(!showUpdateEmail
                ? []
                : [
                    {
                      key: `update_user_email`,
                      width: 240,
                      onClick: () => {
                        setDialog(`change_email`);
                      },
                      content: <Text intlId="administration.users.edit.email" style={{ cursor: `pointer` }} />,
                    },
                  ]),

              ...(!showUpdatePassword
                ? []
                : [
                    {
                      key: `update_user_password`,
                      width: 240,
                      onClick: () => {
                        setDialog(`change_password`);
                      },
                      content: <Text intlId="administration.users.edit.password" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(!showValidate || user.validation.email_validated
                ? []
                : [
                    {
                      key: `validate_update`,
                      width: 240,
                      onClick: () => {
                        validateUser({ user });
                      },
                      content: <Text intlId="administration.users.validate" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(!showManageSMS
                ? []
                : [
                    {
                      key: `manage_organization_sms`,
                      width: 240,
                      onClick: () => {
                        setDialog(`manage_sms_limit`);
                      },
                      content: <Text intlId="administration.users.manage_sms.title" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(!showManageGeocodeCredit
                ? []
                : [
                    {
                      key: `manage_organization_geocode_credit`,
                      width: 240,
                      onClick: () => {
                        setDialog(`manage_geocode_credit`);
                      },
                      content: (
                        <Text intlId="administration.users.manage_geocode_credit.title" style={{ cursor: `pointer` }} />
                      ),
                    },
                  ]),
              ...(!showManageContract
                ? []
                : [
                    {
                      key: `manage_organization_contract`,
                      width: 240,
                      onClick: () => {
                        setDialog(`manage_contract`);
                      },
                      content: (
                        <Text intlId="administration.users.manage_contract.title" style={{ cursor: `pointer` }} />
                      ),
                    },
                  ]),
              ...(!showChangePlan ||
              user.organization.subscription.status === `canceled` ||
              !user.organization.subscription.stripe_customer_id
                ? []
                : [
                    {
                      key: `change_plan`,
                      width: 240,
                      onClick: () => {
                        window.open(
                          generateStripeCustomerLink(user.organization.subscription.stripe_customer_id!),
                          `_blank`
                        );
                      },
                      content: <Text intlId="administration.users.change_plan" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(!showChangeExtraLimits
                ? []
                : [
                    {
                      key: `change_extra_limits`,
                      width: 240,
                      onClick: () => {
                        setDialog(`change_extra_limits`);
                      },
                      content: (
                        <Text intlId="user_profile_summary.change_plan.extra_limits" style={{ cursor: `pointer` }} />
                      ),
                    },
                  ]),
              ...(!showCreateSubscriptionTrial || user.organization.subscription.status !== `canceled`
                ? []
                : [
                    {
                      key: `create_trial_subscription`,
                      width: 240,
                      onClick: () => {
                        setDialog(`create_trial_subscription`);
                      },
                      content: (
                        <Text intlId="administration.users.create_trial_subscription" style={{ cursor: `pointer` }} />
                      ),
                    },
                  ]),
              ...(!showCancelSubscription ||
              user.deleted ||
              user.organization.subscription.status === `canceled` ||
              user.organization.subscription.plan === `drivers:lite`
                ? []
                : [
                    null,
                    {
                      key: `cancel_subscription`,
                      width: 240,
                      onClick: () => {
                        if (
                          user.organization.subscription.status === `trialing` ||
                          user.organization.subscription.plan === `drivers:free`
                        ) {
                          setDialog(`cancel_subscription`);
                        } else {
                          window.open(
                            generateStripeCustomerLink(user.organization.subscription.stripe_customer_id!),
                            `_blank`
                          );
                        }
                      },
                      content: (
                        <Text
                          style={{
                            color: RoutalPalette.error.medium,
                            fontSize: `14px`,
                            cursor: `pointer`,
                          }}
                          intlId="administration.users.cancel_subscription"
                        />
                      ),
                    },
                  ]),
            ],
          }}
        >
          {popoverProps?.content}
        </RoutalSelect>
      )}
      {type === `contextual` && (
        <>
          {contextAnchor ? (
            <ContextualMenu
              styledMenuItemStyles={{
                width: `240px`,
              }}
              anchorElementPosition={contextAnchor.screenPosition}
              onClose={() => {
                if (setContextAnchor) {
                  setContextAnchor();
                }
              }}
              options={[
                ...(!showUpdateEmail
                  ? []
                  : [
                      {
                        key: `update_user_email`,
                        text: <Text intlId="administration.users.edit.email" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`change_email`);
                        },
                      },
                    ]),
                ...(!showUpdatePassword
                  ? []
                  : [
                      {
                        key: `update_user_password`,
                        text: <Text intlId="administration.users.edit.password" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`change_password`);
                        },
                      },
                    ]),
                ...(!showValidate || user.validation
                  ? []
                  : [
                      {
                        key: `validate_update`,
                        text: <Text intlId="administration.users.validate" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          validateUser({ user });
                        },
                      },
                    ]),
                ...(!showManageSMS
                  ? []
                  : [
                      {
                        key: `manage_organization_sms`,
                        text: <Text intlId="administration.users.manage_sms.title" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`manage_sms_limit`);
                        },
                      },
                    ]),
                ...(!showManageGeocodeCredit
                  ? []
                  : [
                      {
                        key: `manage_organization_geocode_credit`,
                        text: (
                          <Text
                            intlId="administration.users.manage_geocode_credit.title"
                            style={{ cursor: `pointer` }}
                          />
                        ),
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`manage_geocode_credit`);
                        },
                      },
                    ]),
                ...(!showChangePlan ||
                user.organization.subscription.status === `canceled` ||
                !user.organization.subscription.stripe_customer_id
                  ? []
                  : [
                      {
                        key: `change_plan`,
                        text: <Text intlId="administration.users.change_plan" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();

                          window.open(
                            generateStripeCustomerLink(user.organization.subscription.stripe_customer_id!),
                            `_blank`
                          );
                        },
                      },
                    ]),
                ...(!showChangeExtraLimits
                  ? []
                  : [
                      {
                        key: `change_extra_limits`,
                        text: (
                          <Text intlId="user_profile_summary.change_plan.extra_limits" style={{ cursor: `pointer` }} />
                        ),
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`change_extra_limits`);
                        },
                      },
                    ]),
                ...(!showCreateSubscriptionTrial || user.organization.subscription.status !== `canceled`
                  ? []
                  : [
                      {
                        key: `create_trial_subscription`,
                        text: (
                          <Text intlId="administration.users.create_trial_subscription" style={{ cursor: `pointer` }} />
                        ),
                        onClick: () => {
                          setDialog(`create_trial_subscription`);
                        },
                      },
                    ]),
                ...(!showCancelSubscription || user.organization.subscription.status === `canceled`
                  ? []
                  : [
                      {
                        key: `cancel_subscription`,
                        text: (
                          <Text
                            style={{
                              color: RoutalPalette.error.medium,
                              fontSize: `14px`,
                              cursor: `pointer`,
                            }}
                            intlId="administration.users.cancel_subscription"
                          />
                        ),
                        onClick: (event: any) => {
                          event.stopPropagation();

                          if (user.organization.subscription.status === `trialing`) {
                            setDialog(`cancel_subscription`);
                          } else {
                            window.open(
                              generateStripeCustomerLink(user.organization.subscription.stripe_customer_id!),
                              `_blank`
                            );
                          }
                        },
                      },
                    ]),
              ]}
            />
          ) : null}
        </>
      )}

      {dialog === `change_email` ? (
        <BackofficeClientsUsersEditEmailDialog
          openDialog={dialog === `change_email`}
          user={user}
          onClose={(hasBeenUpdated: boolean) => {
            setDialog(undefined);

            if (setContextAnchor) {
              setContextAnchor();
            }

            if (hasBeenUpdated && refreshAfterUpdate) {
              refreshAfterUpdate();
            }
          }}
        />
      ) : null}
      {dialog === `change_password` ? (
        <BackofficeClientsUsersEditPasswordDialog
          openDialog={dialog === `change_password`}
          user={user}
          onClose={(hasBeenUpdated: boolean) => {
            setDialog(undefined);

            if (setContextAnchor) {
              setContextAnchor();
            }

            if (hasBeenUpdated && refreshAfterUpdate) {
              refreshAfterUpdate();
            }
          }}
        />
      ) : null}
      {dialog === `manage_sms_limit` ? (
        <BackofficeClientsOrganizationsManageSMSDialog
          openDialog={dialog === `manage_sms_limit`}
          organizationId={user.organization.id}
          subscription={user.organization.subscription}
          onClose={(hasBeenUpdated: boolean) => {
            setDialog(undefined);

            if (setContextAnchor) {
              setContextAnchor();
            }

            if (hasBeenUpdated && refreshAfterUpdate) {
              refreshAfterUpdate();
            }
          }}
        />
      ) : null}
      {dialog === `manage_geocode_credit` ? (
        <BackofficeClientsOrganizationsManageGeocodeDialog
          openDialog={dialog === `manage_geocode_credit`}
          organizationId={user.organization.id}
          subscription={user.organization.subscription}
          onClose={(hasBeenUpdated: boolean) => {
            setDialog(undefined);

            if (setContextAnchor) {
              setContextAnchor();
            }

            if (hasBeenUpdated && refreshAfterUpdate) {
              refreshAfterUpdate();
            }
          }}
        />
      ) : null}
      {dialog === `manage_contract` ? (
        <BackofficeClientsOrganizationsManageContractDialog
          openDialog={dialog === `manage_contract`}
          organizationId={user.organization.id}
          subscription={user.organization.subscription}
          onClose={(hasBeenUpdated: boolean) => {
            setDialog(undefined);

            if (setContextAnchor) {
              setContextAnchor();
            }

            if (hasBeenUpdated && refreshAfterUpdate) {
              refreshAfterUpdate();
            }
          }}
        />
      ) : null}

      {dialog === `change_extra_limits` ? (
        <UpdateSubscriptionExtraLimitsDialog
          userId={user.id}
          subscriptionData={{
            extra_vehicles: user.organization.subscription.extra_vehicles,
            extra_supervisors: user.organization.subscription.extra_supervisors,
            extra_projects: user.organization.subscription.extra_projects,
            extra_users: user.organization.subscription.extra_users,
          }}
          onClose={(modified?: boolean) => {
            if (modified && refreshAfterUpdate) {
              refreshAfterUpdate();
            }

            setDialog(undefined);

            if (setContextAnchor) {
              setContextAnchor();
            }
          }}
        />
      ) : null}

      {dialog === `create_trial_subscription` ? (
        <CreateTrialSubscriptionDialog
          organizationId={user.organization.id}
          onClose={(hasBeenUpdated?: boolean) => {
            if (hasBeenUpdated && refreshAfterUpdate) {
              refreshAfterUpdate();
            }

            setDialog(undefined);

            if (setContextAnchor) {
              setContextAnchor();
            }
          }}
        />
      ) : null}

      {dialog === `cancel_subscription` ? (
        <CancelSubscriptionDialog
          userId={user.id}
          plan={user.organization.subscription.plan}
          onClose={(hasCanceled?: boolean) => {
            if (hasCanceled && refreshAfterUpdate) {
              refreshAfterUpdate();
            }

            setDialog(undefined);

            if (setContextAnchor) {
              setContextAnchor();
            }
          }}
        />
      ) : null}
    </>
  );
};
